import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, Button, CircularProgress, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getDeviceTranscript, listDeviceTranscripts } from "../../services/TranscriptService";
import type { StoredTranscript, TranscriptDetail } from "../../services/TranscriptService";
import type { TranscriptTabProps } from "./types";
import * as S from "./styles";

const PAGE_SIZE = 50;

function dateTime(value?: string | number): string {
  if (value === undefined) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function clock(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours ? `${hours}:` : ""}${hours ? String(minutes).padStart(2, "0") : minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export function TranscriptTab(props: TranscriptTabProps) {
  return <TranscriptContent key={JSON.stringify([props.deviceId, props.interventionId, props.accessToken])} {...props} />;
}

function TranscriptContent(props: TranscriptTabProps) {
  const { t } = useTranslation();
  const [transcripts, setTranscripts] = useState<StoredTranscript[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<StoredTranscript | null>(null);
  const [detail, setDetail] = useState<TranscriptDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [detailRetry, setDetailRetry] = useState(0);

  useEffect(() => {
    setTranscripts([]);
    setOffset(0);
    setHasMore(false);
    setSelected(null);
    setDetail(null);
  }, [props.deviceId, props.interventionId, props.accessToken, refresh]);

  useEffect(() => {
    if (!props.deviceId || !props.interventionId || !props.accessToken) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    listDeviceTranscripts(props.deviceId, props.interventionId, props.accessToken, controller.signal, offset, PAGE_SIZE)
      .then((items) => {
        if (controller.signal.aborted) return;
        setTranscripts((previous) => offset === 0 ? items : [
          ...previous,
          ...items.filter((item) => !previous.some((entry) => entry.transcript_id === item.transcript_id)),
        ]);
        setHasMore(items.length === PAGE_SIZE);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        console.error("Failed to load stored transcripts");
        setError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [props.deviceId, props.interventionId, props.accessToken, offset, refresh]);

  useEffect(() => {
    setDetail(null);
    setDetailError(false);
    if (!selected || !props.interventionId || !props.accessToken) return;
    const controller = new AbortController();
    setDetailLoading(true);
    getDeviceTranscript(props.deviceId, props.interventionId, selected.transcript_id, props.accessToken, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setDetail(value); })
      .catch(() => {
        if (controller.signal.aborted) return;
        console.error("Failed to load stored transcript content");
        setDetailError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [selected, props.deviceId, props.interventionId, props.accessToken, detailRetry]);

  if (!props.interventionId) return <S.Content>{t("Select an intervention to view transcripts.")}</S.Content>;
  if (!props.accessToken) return <Alert severity="error">{t("Sign in to view transcripts.")}</Alert>;

  return (
    <S.Root>
      <S.Toolbar>
        {selected ? (
          <>
            <IconButton aria-label={t("Back to transcripts")} onClick={() => setSelected(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2">{selected.filename}</Typography>
          </>
        ) : (
          <>
            <Typography variant="subtitle2">{t("Transcripts")}</Typography>
            <IconButton aria-label={t("Refresh transcripts")} disabled={loading} onClick={() => setRefresh((value) => value + 1)}>
              <RefreshIcon />
            </IconButton>
          </>
        )}
      </S.Toolbar>
      {selected ? (
        <S.Content>
          {detailLoading && <S.Loading><CircularProgress size={24} /></S.Loading>}
          {detailError && <Alert severity="error" action={<Button onClick={() => setDetailRetry((value) => value + 1)}>{t("Retry")}</Button>}>{t("Unable to load transcript content.")}</Alert>}
          {detail && !detailLoading && (
            <>
              <Typography variant="body2" color="text.secondary">
                {dateTime(detail.startedAt ?? detail.timestamp ?? detail.datetime)}
                {detail.durationMs !== undefined && ` \u00b7 ${clock(detail.durationMs)}`}
              </Typography>
              {detail.summary && <><S.Heading variant="subtitle2">{t("Summary")}</S.Heading><S.Text variant="body2">{detail.summary}</S.Text></>}
              <S.Heading variant="subtitle2">{t("Transcript")}</S.Heading>
              {detail.utterances.length ? detail.utterances.map((utterance, index) => (
                <S.Utterance key={index}>
                  <Typography variant="caption" color="text.secondary">
                    {clock(utterance.start)}{utterance.speaker && ` \u00b7 ${utterance.speaker}`}
                  </Typography>
                  <S.Text variant="body2">{utterance.text}</S.Text>
                </S.Utterance>
              )) : detail.text ? <S.Text variant="body2">{detail.text}</S.Text>
                : <Typography color="text.secondary">{t("No transcript text available.")}</Typography>}
            </>
          )}
        </S.Content>
      ) : (
        <>
          {error && <Alert severity="error" action={<Button onClick={() => setRefresh((value) => value + 1)}>{t("Retry")}</Button>}>{t("Unable to load transcripts.")}</Alert>}
          <S.TableScroll>
            <Table size="small" aria-label={t("Stored transcripts")}>
              <TableHead><TableRow><S.HeaderCell>{t("Date")}</S.HeaderCell><S.HeaderCell>{t("Transcript")}</S.HeaderCell></TableRow></TableHead>
              <TableBody>
                {transcripts.map((item) => (
                  <S.Row key={item.transcript_id} hover onClick={() => setSelected(item)}>
                    <TableCell>{dateTime(item.timestamp ?? item.datetime)}</TableCell>
                    <TableCell><Button onClick={() => setSelected(item)}>{item.filename}</Button></TableCell>
                  </S.Row>
                ))}
              </TableBody>
            </Table>
            {loading && <S.Loading><CircularProgress size={24} /></S.Loading>}
            {!loading && !error && !transcripts.length && <S.Content>{t("No transcripts found.")}</S.Content>}
          </S.TableScroll>
          {hasMore && !error && <Button disabled={loading} onClick={() => setOffset((value) => value + PAGE_SIZE)}>{t("Load more")}</Button>}
        </>
      )}
    </S.Root>
  );
}
